import PropTypes from 'prop-types';
import { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, useFormContext } from 'react-hook-form';
import { BsX } from 'react-icons/bs';
import { MdOutlineFileUpload } from 'react-icons/md';
import { fileToBase64 } from '@/utils/utils';
import { PrimaryOutlineButton } from '@/components/button/button-component';
import noCompanyLogo from '@/assets/svg/no-company-logo.svg';
import Loader from '@/components/loader/loader-component';
import RevertIconButton from '@/components/revert-icon-button/revert-icon-button';
import Alert from '../../alert/alert-component';
import Styled from './file-dropzone-style';

/**
 * File DropZone
 * @param { object } props - props
 * @param { string } props.image - image
 * @param { string } props.headerText - headerText
 * @param { string } props.cy - cy
 * @param { string } props.cyDropzone - cyDropzone
 * @param { boolean } props.readOnly - readOnly
 * @param { boolean } props.hideSuccess - hideSuccess
 * @param { boolean } props.noIcon - noIcon
 * @param { boolean } props.newUpload - newUpload
 * @param { object } props.registerOptions - registerOptions
 * @param { string } props.name - name required for React Hook Form
 * @param { boolean } props.toS3 - send the image to S3 bucket or not
 * @param {string} props.path - path to upload image
 * @param { boolean } props.renderPreview - renderPreview
 * @param { boolean } props.noExtras - noExtras
 * @param { boolean } props.onRemove - onRemove
 * @returns { JSX.Element }
 */
export const FileDropZone = ({
  image,
  cy,
  cyDropzone,
  readOnly,
  onChange,
  renderPreview,
  noExtras,
  onRemove,
  raw,
  headerText,
  showRevert,
  onRevert,
}) => {
  const [uploadImage, setUploadImage] = useState();
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);

  const didDrag = useRef(false);

  const uploadFile = useCallback(
    async (fileToUpload) => {
      const file = raw ? fileToUpload : await fileToBase64(fileToUpload);
      const payload = {
        fileName: fileToUpload.name,
        file,
        fileType: fileToUpload.type,
      };
      didDrag.current = false;
      setUploadedImageUrl(payload);
      onChange(payload);
    },
    [onChange, raw],
  );

  // Dropzone START
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length > 1) {
        Alert({
          icon: 'warning',
          text: 'Only the first image will be selected',
        });
      }
      uploadFile(acceptedFiles[0]);
    },
    [uploadFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/pdf': ['.pdf'],
    },
  });
  // Dropzone END

  const setDidDrag = () => {
    didDrag.current = true;
  };

  const clearImage = () => {
    onChange(null);
    setUploadedImageUrl(null);
    setUploadImage(null);
    onRemove();
  };

  const noExtraTextStyle = { color: 'red' };
  const noExtraStyle = { border: 'none', backgroundColor: 'transparent' };

  const renderImage = () => {
    if (uploadedImageUrl?.file) {
      return uploadedImageUrl.file;
    }
    if (image?.file) {
      return image.file;
    }
    return image;
  };

  const renderItem = () => {
    if (renderPreview) {
      if (image || uploadedImageUrl) {
        return (
          <Styled.Column>
            <Styled.Img src={renderImage()} />
            <Styled.ActionWrapper
              $justifyContent={showRevert ? 'flex-start' : 'space-between'}
            >
              <PrimaryOutlineButton
                title="Remove"
                icon={<BsX size={16} />}
                margin="0 0px 0px 0px"
                onClick={clearImage}
                disabled={readOnly}
              />
            </Styled.ActionWrapper>
          </Styled.Column>
        );
      }
      return null;
    }

    if (image || uploadedImageUrl) {
      return (
        <Styled.Column>
          {uploadedImageUrl?.fileName || 'No file name'}
          <Styled.ActionWrapper>
            <PrimaryOutlineButton
              title="Remove"
              icon={<BsX size={16} />}
              margin="0 0px 0px 0px"
              onClick={clearImage}
              disabled={readOnly}
            />
            {showRevert && (
              <RevertIconButton
                onClick={onRevert}
                tooltipMessage="Revert Thumbnail"
              />
            )}
          </Styled.ActionWrapper>
        </Styled.Column>
      );
    }

    return null;
  };

  return (
    <Styled.ImageUploaderHolder>
      <Styled.ImageUploaderHeader>
        <Styled.Label>{headerText}</Styled.Label>
        {showRevert && (
          <RevertIconButton
            onClick={onRevert}
            tooltipMessage="Revert Thumbnail"
            $margin="10px 0 0 0"
          />
        )}
      </Styled.ImageUploaderHeader>

      <Styled.ImageUploaderContainer>
        <Styled.ImgHolder data-cy={cy}>
          {renderItem()}
          {!image && !uploadImage && !uploadedImageUrl && (
            <Styled.DropZone role="none" $noExtras={noExtras}>
              <Styled.Column data-cy={cyDropzone} {...getRootProps()}>
                <Styled.Input {...getInputProps()} />
                {isDragActive ? (
                  <Styled.DropHere>
                    <Styled.Span onDrop={setDidDrag}>
                      Drop the files here ...
                    </Styled.Span>
                  </Styled.DropHere>
                ) : (
                  <>
                    {!noExtras && (
                      <>
                        <Styled.NoCompanyIcon
                          src={noCompanyLogo}
                          alt="No Company Logo"
                        />
                        <Styled.DropHereText>
                          Drag file to upload or
                        </Styled.DropHereText>
                      </>
                    )}
                    <PrimaryOutlineButton
                      style={noExtras ? noExtraStyle : {}}
                      textStyle={noExtras ? noExtraTextStyle : {}}
                      cy="btnBrowseFiles"
                      margin="10px"
                      title="Browse"
                      icon={<MdOutlineFileUpload />}
                      type="button"
                    />
                  </>
                )}
              </Styled.Column>
            </Styled.DropZone>
          )}
        </Styled.ImgHolder>
      </Styled.ImageUploaderContainer>
    </Styled.ImageUploaderHolder>
  );
};

FileDropZone.propTypes = {
  /** Header Text used in the modal */
  headerText: PropTypes.string,
  /** The image */
  image: PropTypes.string,
  /** To Change the image */
  onChange: PropTypes.func.isRequired,
  /** Cypress data-cy attribute */
  cy: PropTypes.string,
  /** Cypress data-cy attribute for the dropzone */
  cyDropzone: PropTypes.string,
  /** Read only mode */
  readOnly: PropTypes.bool,
  /** Render the preview */
  renderPreview: PropTypes.bool,
  /** Should the raw file be returned or a base64 */
  raw: PropTypes.bool,
  /** No Extras */
  noExtras: PropTypes.bool,
  /** On Remove */
  onRemove: PropTypes.func,
  /** Show revert button */
  showRevert: PropTypes.bool,
  /** On Revert */
  onRevert: PropTypes.func,
};

FileDropZone.defaultProps = {
  headerText: '',
  cy: undefined,
  cyDropzone: undefined,
  readOnly: false,
  renderPreview: true,
  raw: false,
  image: null,
  noExtras: false,
  onRemove: () => {},
  showRevert: false,
  onRevert: () => {},
};

/**
 * Image Uploader
 * @param { object } props - props
 * @param { string } props.image - image
 * @param { string } props.headerText - headerText
 * @param { boolean } props.noExtras - removes some icons and changes styling
 * @param { string } props.cy - cy
 * @param { string } props.cyDropzone - cyDropzone
 * @param { boolean } props.readOnly - readOnly
 * @param { boolean } props.hideSuccess - hideSuccess
 * @param { boolean } props.noIcon - noIcon
 * @param { boolean } props.newUpload - newUpload
 * @param { object } props.registerOptions - registerOptions
 * @param { string } props.name - name required for React Hook Form
 * @param { boolean } props.toS3 - send the image to S3 bucket or not
 * @param {string} props.path - path to upload image
 * @param { boolean } props.onRemove - onRemove
 * @returns { JSX.Element }
 */
const FileUploader = ({
  headerText,
  cy,
  cyDropzone,
  readOnly,
  name,
  registerOptions,
  renderPreview,
  noExtras,
  loading,
  onRemove,
  onChange,
}) => {
  const { control } = useFormContext();

  if (loading) {
    return <Loader loading />;
  }

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={null}
      rules={registerOptions}
      render={({ field: { value, onChange: fieldOnChange } }) => (
        <FileDropZone
          headerText={headerText}
          cy={cy}
          renderPreview={renderPreview}
          onChange={(files) => {
            fieldOnChange(files);
            if (onChange) {
              onChange(files);
            }
          }}
          cyDropzone={cyDropzone}
          readOnly={readOnly}
          image={value}
          noExtras={noExtras}
          onRemove={() => {
            fieldOnChange(null);
            if (onRemove) {
              onRemove();
            }
          }}
        />
      )}
    />
  );
};

FileUploader.propTypes = {
  /** Header Text used in the modal */
  headerText: PropTypes.string,
  /** Cypress data-cy attribute */
  cy: PropTypes.string,
  /** Cypress data-cy attribute for the dropzone */
  cyDropzone: PropTypes.string,
  /** Read only mode */
  readOnly: PropTypes.bool,
  /** Register options */
  registerOptions: PropTypes.shape({}),
  /** React Hook Form name */
  name: PropTypes.string.isRequired,
  /** Render the preview */
  renderPreview: PropTypes.bool,
  /** No Extras */
  noExtras: PropTypes.bool,
  /** Loading */
  loading: PropTypes.bool,
  /** On Remove */
  onRemove: PropTypes.func,
  /** onChange callback */
  onChange: PropTypes.func,
  /** Show revert button */
};

FileUploader.defaultProps = {
  headerText: '',
  cy: undefined,
  cyDropzone: undefined,
  readOnly: false,
  renderPreview: true,
  registerOptions: {
    onChange: () => {},
  },
  noExtras: false,
  loading: false,
  onRemove: () => {},
  onChange: undefined,
};

export default FileUploader;
